import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  useDimensions,
  ASPECT_RATIO_BREAKPOINTS,
} from "../../../../Contexts/DimensionsContext";
import {
  closeReplay,
  closeMenu,
  fetchRoundsHistory,
  clearRoundsHistory,
  openReplayEnter,
  closeReplayEnter,
  closeReplayEnded,
  setEnterRound,
  clearEnterRound,
  setEndedRound,
  clearEndedRound,
  startReplaySession,
  endReplaySession,
  fetchReplayInit,
} from "../../../../State/actions";
import {
  selectReplayActive,
  selectReplayRounds,
  selectReplayHistoryLoading,
  selectReplayHistoryEmpty,
  selectReplayHistoryError,
  selectReplayEnterActive,
  selectReplayEndedActive,
  selectEnterRound,
  selectEndedRound,
  selectIsStandaloneReplay,
  selectReplayRoundId,
  selectCurrency,
} from "../../../../State/selectors";
import { handleEnterReplayMode, handleExitReplayMode } from "../../../../service";
import { isStandaloneReplayMode } from "../../../../utils/launcherOptions";
import { selectLoaderFadeMidpoint } from "../../../../State/Slices/loadingProgressSlice";
import { formatToCustomLocale } from "../../../../utilities";
import { useIosOverlayGuard } from "../../../../Hooks/useIosOverlayGuard";
import { useOverlayFade } from "../../../../Hooks/useOverlayFade";
import {
  getUITextureAsDataURL,
  getNewUITextureAsDataURL,
} from "../../../../assets";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import {
  getPaytableShellLayout,
  getOverlayDeviceBuckets,
} from "./paytableConfig";

/**
 * ReplayOverlay — the History panel (round list; replay playback comes later).
 *
 * Full-viewport HTML overlay sharing the PaytableOverlay lifecycle: same fade
 * in/out, same iOS-overlay guard, same device buckets (getOverlayDeviceBuckets).
 * The gray panel is sized purely by per-device padding (panelPad) on the overlay
 * — top/bottom set its height, side its width. Inside: a two-row header (title
 * toolbar + column-labels toolbar) pinned on top, and a scrollable body of round
 * rows below. The close button is absolutely positioned against the overlay
 * (btnAnchorTop reproduces the old paytable title-row center) with per-device
 * nudges, so it's independent of the panel insets.
 *
 * Rows come from the rounds-history API via replaySlice — fetched on open, with
 * a loading state in the body while the request is in flight.
 */
// Row/modal formatters. The slice holds raw subunits + ISO strings; formatting
// happens at render — amounts via formatToCustomLocale + the currency code from
// state.game (same as BalanceDisplay), dates via dayjs (same as Clock).

/** Table column: "08/05/2026, 10:22:49" (local time). */
const formatRowTime = (iso) => {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format("MM/DD/YYYY, HH:mm:ss") : String(iso);
};

/**
 * Enter/Ended modal: "August 5, 2026 at 10:22:49 AM" (local time).
 * With no timestamp, falls back to the round's own id ("#50314398") and then
 * to an em dash. A standalone replay link has no history row behind it and so
 * no date; the reference shows the round id in that slot, and only has an id
 * to show once a step has reported one, hence the second fallback. Either way
 * the row stays, which keeps the modal's layout identical.
 */
const formatPlayedAt = (iso, roundId) => {
  if (!iso) return roundId != null && roundId !== "" ? `#${roundId}` : "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("MMMM D, YYYY [at] hh:mm:ss A") : String(iso);
};

/** Signed round ids are long — show head…tail like the reference design. */
const shortenRoundId = (id) => {
  if (!id) return "";
  const s = String(id);
  return s.length <= 14 ? s : `${s.slice(0, 8)}…${s.slice(-4)}`;
};

/**
 * Multiplier column: win ÷ the bet the round was actually PLAYED at.
 *
 * ── `initialBet` first. Confirmed against PopiPlay's own games, 2026-09-10 ──
 *
 * The two fields differ only on a BOUGHT round, and they mean different things:
 *
 *   bet          what the player PAID — the purchase price of the bonus
 *   initial_bet  the stake each spin was played at
 *
 * A reference row from another of their games settles which one belongs here:
 *
 *   Bet 14.50 FUN    Win 31.88 FUN    x318.80    (star)
 *
 * 31.88 ÷ 0.10 = 318.80, so the multiplier is against the STAKE while the Bet
 * column keeps showing the price. Ours now matches: a 159.97 win on a 0.10
 * stake bought for 61.00 reads x1599.70, not x2.62.
 *
 * I had this backwards. It was briefly changed to divide by `bet`, because a
 * bought round that LOSES then shows a green multiplier — measured on 1455068,
 * `bet 1900 / initial_bet 10 / win 288 / profit -1612` printed x28.80 beside a
 * 16.12 loss. That is real, and it is also what their games do: the multiplier
 * answers "how many times the stake did this pay", not "did the player profit".
 * The star marks the round as bought so the price is accounted for.
 *
 * `bet` stays as the FALLBACK for a row that carries no initial_bet.
 */
const formatMultiplier = (win, base) =>
  win > 0 && base > 0 ? `x${(win / base).toFixed(2)}` : "x0";

/**
 * Copy text to the clipboard, on any origin.
 *
 * ── Why this is not just navigator.clipboard ──
 *
 * `navigator.clipboard` only exists in a SECURE CONTEXT: https, or localhost.
 * Open the same build on a LAN address — http://192.168.x.x:3000, which is how
 * a tablet or a phone on the desk reaches the dev server — and the whole API is
 * `undefined`. The old code guarded on that and then did nothing, so the button
 * was dead on exactly the devices most likely to be testing replay, while
 * working perfectly on the laptop it was written on.
 *
 * The fallback is the pre-clipboard-API way: a throwaway off-screen textarea,
 * select, `document.execCommand("copy")`. Deprecated, still implemented
 * everywhere, and it has no secure-context requirement.
 *
 * @param   {string} text
 * @returns {Promise<boolean>} whether the copy actually happened
 */
const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied, or not user-initiated — fall through and try the
      // old way rather than giving up.
    }
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    // Off-screen, and readOnly so mobile keyboards do not appear.
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.top = "-9999px";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length); // iOS needs the explicit range
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    if (!ok) console.warn("[replay] Copy link: the browser refused the copy.");
    return ok;
  } catch (error) {
    console.warn("[replay] Copy link failed:", error);
    return false;
  }
};

const ReplayOverlay = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const replayActive = useSelector(selectReplayActive);
  const rounds = useSelector(selectReplayRounds);
  const historyLoading = useSelector(selectReplayHistoryLoading);
  const historyEmpty = useSelector(selectReplayHistoryEmpty);
  const historyError = useSelector(selectReplayHistoryError);
  const currency = useSelector(selectCurrency);
  const { isHidden } = useIosOverlayGuard();
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    uiSymbolSize,
    isMobileSmall,
  } = useDimensions();

  // Shared device buckets (single source in paytableConfig — kept in lock-step
  // with getPaytableShellLayout / PaytableOverlay). Tablets route into the
  // standard-mobile buckets; toolbar-shortened standard portrait → small portrait.
  const {
    isDesktop,
    isPortrait,
    isStandardMobilePortrait,
    isStandardMobileLandscape,
    isSmallMobilePortrait,
    isSmallMobileLandscape,
    isTabletPortrait,
    isTabletLandscape,
  } = getOverlayDeviceBuckets({ device, orientation, isMobileSmall, dimensions });
  const isWideDesktop =
    isDesktop &&
    dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH;

  // Mount + fade lifecycle — shared hook (animejs on the RAF ticker, no timers).
  const { shouldRender, overlayRef } = useOverlayFade({
    active: replayActive,
    isHidden,
    onClosed: () => dispatch(clearRoundsHistory()),
  });

  // Enter modal. enterActive drives its fade; enterRow holds the round and is
  // cleared only once the fade-out finishes, so the content stays put on the
  // way out. Both live in Redux (replayEnterActive in userInterfaceSlice,
  // enterRound in replaySlice) — same split every other popup uses.
  const enterActive = useSelector(selectReplayEnterActive);
  const enterRow = useSelector(selectEnterRound);
  const { shouldRender: enterShouldRender, overlayRef: enterRef } =
    useOverlayFade({
      active: enterActive,
      isHidden,
      onClosed: () => dispatch(clearEnterRound()),
      // Standalone replay link: this modal is opened at the midpoint of the
      // loader's fade-out (see the effect below), so the two cross-fade. The
      // hook's usual pre-fade delay would reintroduce the gap that crossing
      // them is meant to remove. In-game (hot-swap) keeps the shared default
      // — there it opens over an already-visible board, where it looks right.
      fadeInDelay: isStandaloneReplayMode() ? 0 : undefined,
    });

  // Ended modal — shown once a replay finishes playing (spec §4/§8). Same
  // frame/fade lifecycle and Redux split as the Enter modal above; endedRow
  // holds the round whose replay just ended, so the stats stay on screen
  // through the fade-out. Unlike the Enter modal, this one is NOT auto-cleared
  // on close: "Replay Again" closes it and expects it to reopen for the same
  // round once that replay finishes again, and the close-fade's completion
  // (a few hundred ms later, per CONTENT_ANIM_DURATION) would race that
  // reopen and wipe endedRow out from under it. It's always overwritten via
  // setEndedRound before every real open anyway, so clearing on close only
  // ever bought memory hygiene — handleClose does that explicitly on full exit.
  const endedActive = useSelector(selectReplayEndedActive);
  const endedRow = useSelector(selectEndedRound);

  // Standalone replay link entry point (docs/replay-history-spec.md §0
  // Stage 9/12) — no live game or History board underneath, so the Ended
  // modal's close behavior differs (see handleEndedClose below).
  const isStandaloneReplay = useSelector(selectIsStandaloneReplay);
  const replayRoundId = useSelector(selectReplayRoundId);
  // Drives the standalone Enter-modal opener below — see that effect for why
  // it hooks the loader's fade midpoint rather than its completion.
  const loaderFadeMidpoint = useSelector(selectLoaderFadeMidpoint);
  const { shouldRender: endedShouldRender, overlayRef: endedRef } =
    useOverlayFade({
      active: endedActive,
      isHidden,
    });


  // ── Bet / Total Win / X Bet: ONE LINE, always ──
  //
  // The three stats must never wrap and never break a word, so when they do not
  // fit they are scaled down together until they do.
  //
  // MEASURED, because nothing else can know. The shrink depends on the STRINGS
  // — "19,000.00 EUR" is two and a half times "5.00 EUR" — so no CSS rule, no
  // viewport unit and no container query can work it out.
  //
  // ── Measured from the CHILDREN, not from the row's scrollWidth ──
  //
  // The row is `justify-content: center`, and a centred flex row overflows
  // BOTH edges. `scrollWidth` can only see the right-hand half — the left
  // overflow is outside the scrollable origin and is simply not counted — so it
  // reports roughly half the excess and the correction came out about 2x too
  // weak. Adding the items up is exact and does not care how they are aligned.
  //
  // `offsetWidth`, not `getBoundingClientRect()`: the rect is AFTER the
  // transform, so measuring with it would shrink the measurement along with the
  // row and settle somewhere arbitrary. offsetWidth is layout, which no
  // transform touches — which is also why the fit is applied as a transform in
  // the first place.
  const endedStatsRef = useRef(null);
  const [endedStatsScale, setEndedStatsScale] = useState(1);
  useLayoutEffect(() => {
    const el = endedStatsRef.current;
    if (!el) return;
    const available = el.clientWidth;
    const kids = Array.from(el.children);
    if (!(available > 0) || kids.length === 0) return;

    const gap = parseFloat(window.getComputedStyle(el).columnGap) || 0;
    // Portrait stacks them, so the widest ONE is what has to fit; landscape
    // puts them side by side, so it is the sum plus the gaps between.
    const needed = isPortrait
      ? kids.reduce((w, k) => Math.max(w, k.offsetWidth), 0)
      : kids.reduce((w, k) => w + k.offsetWidth, 0) + gap * (kids.length - 1);
    if (!(needed > 0)) return;

    // A hair under 1 so a rounding difference cannot leave one pixel clipped.
    setEndedStatsScale(needed > available ? (available / needed) * 0.99 : 1);
  }, [
    // The modal MOUNTS a commit after `endedActive` turns true — useOverlayFade
    // sets its render flag from an effect — so keying this on `endedActive`
    // alone measured a row that did not exist yet and left the scale at 1 for
    // the whole time it was on screen. This flag is the one that says the
    // element is really there.
    endedShouldRender,
    endedRow?.bet,
    endedRow?.win,
    endedRow?.initialBet,
    currency,
    isPortrait,
    // Re-measured on every resize: the modal is a percentage of the panel, and
    // the font size is a tier multiple, so both edges of the fit can move.
    dimensions?.width,
    dimensions?.height,
  ]);

  const gameTitle =
    window.__OPTIONS__?.title || window.__OPTIONS__?.identifier || "";

  // Gated on ALL THREE render flags, not just the History board's own
  // `shouldRender` — the Enter/Ended modals draw the same close (X) icon and
  // can be on screen with History closed behind them. That is the permanent
  // state in standalone replay mode (§0 Stage 9/11: the History board never
  // mounts at all), where gating on `shouldRender` alone left the modals'
  // X button with no background image. Same class of bug as the
  // overlayStyles memo below — anything the modals need must not hang off
  // the History board's visibility.
  const anyReplayUiVisible = shouldRender || enterShouldRender || endedShouldRender;
  const closeIconSrc = useMemo(
    () => (anyReplayUiVisible ? getUITextureAsDataURL("button_x") : ""),
    [anyReplayUiVisible],
  );
  const historyIconSrc = useMemo(
    () => (anyReplayUiVisible ? getNewUITextureAsDataURL("icon_replay") : ""),
    [anyReplayUiVisible],
  );

  // Every layout number is the paytable's — see getPaytableShellLayout.
  const {
    shellTopPad,
    shellBottomPad,
    closeButtonSize,
    closeButtonRight,
    headerTitleFontSize,
  } = useMemo(
    () =>
      getPaytableShellLayout({
        device,
        orientation,
        dimensions,
        uiSymbolScale,
        uiSymbolSize,
        isMobileSmall,
        isWideDesktop,
      }),
    [
      device,
      orientation,
      dimensions,
      uiSymbolScale,
      uiSymbolSize,
      isMobileSmall,
      isWideDesktop,
    ],
  );

  // ── Close-button nudge (per device/orientation) ──
  // The shared paytable shell puts the X where the paytable wants it; the replay
  // reference sits it higher (on the ruler) and closer to the right edge. These
  // are replay-only offsets so PaytableOverlay is untouched. Desktop tuned first;
  // other devices/orientations get their own values as we work through them.
  //   up    → px to pull the button upward (subtracted from top:50%)
  //   right → px to push the button toward the right edge (subtracted from `right`)
  //   scale → multiplier on the shared close-button size (1 = paytable default)
  const closeBtnNudge = useMemo(() => {
    // Wide desktop is its own tier (checked before isDesktop, which it also
    // satisfies) — the wider panel leaves the X too far inboard, so it gets
    // pushed further toward the edge and sized up to sit on the ruler.
    if (isWideDesktop) return { up: 18, right: 148, scale: 1.12 };
    if (isDesktop) return { up: 18, right: 100, scale: 0.92 };
    // Standard mobile portrait: paytable pushed the X hard to the edge to avoid
    // overlap; replay has no such need, so pull it inward (negative right) + up.
    if (isStandardMobilePortrait || isTabletPortrait)
      return { up: 8, right: -14, scale: 1 };
    if (isSmallMobilePortrait) return { up: 4, right: -10, scale: 1 };
    // Standard mobile landscape: nudge up and outward (positive right = toward edge).
    if (isStandardMobileLandscape || isTabletLandscape)
      return { up: 8, right: 40, scale: 1 };
    if (isSmallMobileLandscape) return { up: 6, right: 40, scale: 1 };
    return { up: 0, right: 0, scale: 1 };
  }, [
    isWideDesktop,
    isDesktop,
    isStandardMobilePortrait,
    isSmallMobilePortrait,
    isStandardMobileLandscape,
    isSmallMobileLandscape,
    isTabletPortrait,
    isTabletLandscape,
  ]);

  const closeBtnPx = Math.round(closeButtonSize * closeBtnNudge.scale);

  // The X used to sit at 50% of the (now-removed) invisible header title row.
  // Reproduce that same vertical anchor as an absolute top so the tuned per-device
  // nudges still hold: the title row started at shellTopPad and its line box was
  // ~headerTitleFontSize tall, so its center ≈ shellTopPad + fontSize*0.6.
  const btnAnchorTop = shellTopPad + Math.round(headerTitleFontSize * 0.6);

  // ── Gray panel insets (per device/orientation) ──
  // The panel fills the overlay minus these paddings: top/bottom set its height,
  // side sets its width. Replaces the old header + centered-% model. Values are
  // px computed from the live viewport so they scale within a bucket. Tune per
  // bucket against the reference; default is roughly the old content box.
  const panelPad = useMemo(() => {
    const dflt = { top: shellTopPad, bottom: shellBottomPad, side: 0 };
    // Wide desktop (≥ WIDE_DESKTOP_MIN_WIDTH, 2540px) is its own tier, same
    // split Paytable/GameRules use — checked BEFORE isDesktop, which it also
    // satisfies. Side padding is what sets the panel's width
    // (panel = viewport − 2×side), so it's the one dial per tier.
    if (isWideDesktop)
      return {
        top: Math.round(dimensions.height * 0.095),
        bottom: shellBottomPad,
        side: Math.round(dimensions.width * 0.1475),
      };
    if (isDesktop)
      return {
        top: Math.round(dimensions.height * 0.095),
        bottom: shellBottomPad,
        side: Math.round(dimensions.width * 0.17),
      };
    if (isStandardMobilePortrait || isTabletPortrait)
      return {
        top: Math.round(dimensions.height * 0.14),
        bottom: Math.round(dimensions.height * 0.21),
        side: Math.round(dimensions.width * 0.03),
      };
    if (isSmallMobilePortrait)
      return {
        top: Math.round(dimensions.height * 0.05),
        bottom: Math.round(dimensions.height * 0.14),
        side: Math.round(dimensions.width * 0.02),
      };
    if (isStandardMobileLandscape || isTabletLandscape)
      return {
        top: Math.round(dimensions.height * 0.11),
        bottom: shellBottomPad,
        side: Math.round(dimensions.width * 0.19),
      };
    if (isSmallMobileLandscape)
      return {
        top: Math.round(dimensions.height * 0.0985),
        bottom: Math.round(dimensions.height * 0.0775),
        side: Math.round(dimensions.width * 0.115),
      };
    return dflt;
  }, [
    shellTopPad,
    shellBottomPad,
    dimensions.height,
    dimensions.width,
    isWideDesktop,
    isDesktop,
    isStandardMobilePortrait,
    isSmallMobilePortrait,
    isStandardMobileLandscape,
    isSmallMobileLandscape,
    isTabletPortrait,
    isTabletLandscape,
  ]);

  // Header row heights per device: top = lighter #161616 title toolbar,
  // bottom = dark #0e0e0e column-labels toolbar. Plain scalars (no useMemo needed).
  const isMobilePortraitHdr = isStandardMobilePortrait || isSmallMobilePortrait;
  const isMobileLandscapeHdr =
    isStandardMobileLandscape || isSmallMobileLandscape;
  const headerTopPx = isDesktop
    ? 46
    : isMobilePortraitHdr
      ? 58
      : isMobileLandscapeHdr
        ? 44
        : 42;
  const headerBottomPx = isDesktop
    ? 38
    : isMobilePortraitHdr
      ? 34
      : isMobileLandscapeHdr
        ? 32
        : 42;

  // Handle close — also fades out any open enter/ended modal.
  // Also the safety-valve exit while the real Ended-modal-driven flow isn't
  // wired yet (Stage 5): a no-op if replay was never engaged, but if it was,
  // this is currently the only way to flip the RGS object back to live mode
  // — handleEnterPlay closes this whole board immediately as a stand-in, so
  // without this, replayMode would stay stuck true with no UI path to undo it.
  const handleClose = useCallback(async () => {
    soundManager.play(SOUND_NAME.button_click);
    // ── AWAITED, and it must stay awaited ──
    //
    // handleExitReplayMode is async (it resolves the provider first), so
    // firing it un-awaited flipped Redux to LIVE while the RGS object was
    // still in replay for another microtask. Any command sent in that window
    // is judged live by the UI and replayed by the transport — and the
    // dangerous direction is the same race one tick later: a feature command
    // built from replayed state, sent LIVE to a session that is not in that
    // feature. The server answers 202, and two of those is a ban.
    //
    // Awaiting closes the window: both sides are in replay until the flip,
    // then both are live.
    // ── If this throws, the player is STRANDED, so say so out loud ──
    //
    // Everything below is skipped on a throw: the session stays active, the
    // indicator keeps blinking, the round is never restored, and
    // `replayFinished` keeps ButtonPlay's isReplayLocked returning early — a
    // dead spin button with no way out but a reload. Three symptoms, one
    // unhandled rejection, and nothing in the console to connect them.
    //
    // Rethrown deliberately: the exit is NOT forced through here. Flipping
    // Redux to live while the RGS object may still be in replay is the exact
    // mismatch this await exists to prevent — see the note above. A stranded
    // player is recoverable with a reload; a command sent on the wrong side of
    // that flip is a 202, and two of those is a ban.
    try {
      await handleExitReplayMode();
    } catch (error) {
      console.warn(
        "[replay] Exit failed — session left active and the spin button locked. " +
          "Deliberately not forced through; see handleClose.",
        error,
      );
      throw error;
    }
    dispatch(endReplaySession());
    dispatch(closeReplayEnter());
    dispatch(closeReplayEnded());
    // Ended modal's row is no longer auto-cleared on close (see its
    // useOverlayFade call above) — this real, full exit is where it
    // actually needs cleaning up.
    dispatch(clearEndedRound());
    dispatch(closeReplay());
    dispatch(closeMenu());
  }, [dispatch]);

  // A row's Play button → open the enter modal for that round.
  const handlePlay = useCallback(
    (row) => {
      soundManager.play(SOUND_NAME.button_click);
      dispatch(setEnterRound(row));
      dispatch(openReplayEnter());
    },
    [dispatch],
  );

  // Enter modal — X closes just this modal (History stays open behind it).
  const handleEnterClose = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(closeReplayEnter());
  }, [dispatch]);

  // Enter modal — big Play button (docs/replay-history-spec.md §0, Stage 1).
  // Engages replay mode, fetches that round's step sequence, and closes both
  // this modal AND the History board underneath — same as the board's own X
  // — so the game itself is revealed, not the round list. Awaits
  // handleEnterReplayMode before dispatching the init fetch: the fetch needs
  // this.replayUrl already set on the RGS object, and firing both without
  // awaiting would race two separate async calls with no guaranteed order.
  const handleEnterPlay = useCallback(async () => {
    soundManager.play(SOUND_NAME.button_click);
    if (!enterRow?.replayUrl) {
      // No resolvable replay URL for this row (_extractReplayUrl's game-param
      // fallback came up empty) — nothing to actually play. Bail out here
      // rather than starting a session anyway: sessionActive would go true
      // while the RGS object's replayMode stays false, so Spin would fall
      // through to a REAL live spin (real bet/balance) while the UI still
      // thinks — and displays — a replay.
      dispatch(closeReplayEnter());
      return;
    }
    await handleEnterReplayMode(enterRow.replayUrl);
    // startReplaySession FIRST, before anything can write replay data into the
    // feature slices: freeRoundsSlice snapshots the live campaign on this
    // action so it can hand it back on exit. fetchReplayInit's result would
    // overwrite that campaign, so the snapshot has to be taken before it lands.
    // (It resolves asynchronously so the old order happened to work, but
    // relying on that is a trap for whoever reorders these next.)
    dispatch(startReplaySession());
    dispatch(fetchReplayInit());
    // Pre-stage the Ended modal's data now, not when it actually opens —
    // enterRound gets cleared once the Enter modal's own close-fade finishes
    // (long before the replay session ends), so this is the one point where
    // we're guaranteed to still have the round's bet/win/time/link on hand.
    dispatch(setEndedRound(enterRow));
    dispatch(closeReplayEnter());
    dispatch(closeReplay());
    dispatch(closeMenu());
  }, [dispatch, enterRow]);

  // Ended modal — REPLAY button. Plays the exact same round again from the
  // top: re-fetching its step sequence resets the RGS object's step cursor
  // (fetchReplayInit always zeroes replayStepCursor/replayLastStepId, same
  // as a fresh entry — see RGS.js), and its balance.wallet snapshot puts
  // replayBalance back at the round's pre-win starting point (same
  // subtraction fetchReplayInit.fulfilled always does). startReplaySession
  // clears replayFinished so handleSpin's guard opens back up. Sourced from
  // endedRow, not enterRow — enterRound is already cleared by the time this
  // modal is showing. Also closes the History board that auto-reopened
  // behind this modal (see ReelControlContext's replay-finished effect) —
  // same closing sequence handleEnterPlay uses to reveal the game itself.
  //
  // Standalone replay link (§0 Stage 9/12): the RGS object's replayUrl was
  // already set at boot and is never cleared, so this just re-fetches
  // against it directly — no History/Menu to close, nothing to re-enter.
  // Known gap: this doesn't reset state.game.balance/bet back to the
  // round's starting values (only initFromReplayLink at boot does that) —
  // the live balance/bet stay wherever the just-finished round left them.
  // Low priority until this needs its own fix.
  const handleReplayAgain = useCallback(async () => {
    soundManager.play(SOUND_NAME.button_click);
    if (isStandaloneReplay) {
      // Await the re-fetch before clearing replayFinished: fetchReplayInit is
      // what resets the RGS object's step cursor back to step one, and
      // startReplaySession is what re-opens handleSpin's guard. Clearing the
      // guard first would leave a window where a fast Spin reaches a cursor
      // that's still exhausted, and _replaySpin would throw "no more steps"
      // — with no live game underneath to recover into.
      await dispatch(fetchReplayInit());
      dispatch(startReplaySession({ standalone: true }));
      dispatch(closeReplayEnded());
      return;
    }
    if (!endedRow?.replayUrl) {
      // Same guard as handleEnterPlay — no resolvable URL means nothing to
      // replay, don't start a session the RGS object never actually enters.
      dispatch(closeReplayEnded());
      return;
    }
    await handleEnterReplayMode(endedRow.replayUrl);
    // Same ordering rule as handleEnterPlay: startReplaySession before any
    // replay data lands. Harmless here (the snapshot is already taken and both
    // slices guard against re-snapshotting), but kept consistent so the
    // invariant holds wherever a session is (re)started.
    dispatch(startReplaySession());
    dispatch(fetchReplayInit());
    dispatch(closeReplayEnter());
    dispatch(closeReplayEnded());
    dispatch(closeReplay());
    dispatch(closeMenu());
  }, [dispatch, endedRow, isStandaloneReplay]);

  // Ended modal — X closes just this modal (History stays open behind it).
  // Standalone replay link: no History board to reveal, so X re-launches
  // the same replay instead (spec §4's standalone behavior table) — same
  // action as the REPLAY button, via handleReplayAgain.
  const handleEndedClose = useCallback(() => {
    if (isStandaloneReplay) {
      handleReplayAgain();
      return;
    }
    soundManager.play(SOUND_NAME.button_click);
    dispatch(closeReplayEnded());
  }, [dispatch, isStandaloneReplay, handleReplayAgain]);

  // Ended modal — copy the round's full shareable replay link (spec §6.3).
  // Dev builds only: copy a LOCAL standalone-replay test link instead of the
  // real production one (docs/replay-history-spec.md §0 Stage 9) — the
  // production replayLink always opens PopiPlay's deployed bundle, which
  // never has whatever's running locally. This uses the exact mechanism
  // public/loader/PopiPlay.js already reads (?replayUrl= on the dev
  // server's own address), pointed at this round's real, already-extracted
  // replayUrl — same real replay data, but running today's local code.
  const handleCopyLink = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    const isDevBuild = process.env.NODE_ENV !== "production";
    // A standalone replay page was itself opened from a shared replay link,
    // so its own URL is the thing to share onward — no history row to take a
    // signed replayLink from, and none needed.
    const link = isStandaloneReplay
      ? window.location.href
      : isDevBuild && endedRow?.replayUrl
        ? `${window.location.origin}${window.location.pathname}?replayUrl=${encodeURIComponent(endedRow.replayUrl)}`
        : endedRow?.replayLink;
    if (!link) {
      // Nothing to copy: the row carried no `replay_link` from the API. Worth
      // saying out loud — silently doing nothing is indistinguishable from a
      // clipboard failure, and the two have completely different fixes.
      console.warn("[replay] Copy link: no link on this round.", {
        isStandaloneReplay,
        replayUrl: endedRow?.replayUrl ?? null,
        replayLink: endedRow?.replayLink ?? null,
      });
      return;
    }
    copyTextToClipboard(link);
  }, [endedRow, isStandaloneReplay]);

  // Refetch whenever the panel opens — the signed replay links in each row are
  // re-issued per request, so a cached list would hand out dead links.
  useEffect(() => {
    if (replayActive) dispatch(fetchRoundsHistory());
  }, [replayActive, dispatch]);

  // Standalone replay link (docs/replay-history-spec.md §0 Stage 9/10) — open
  // the Enter modal once, at the midpoint of the loader's fade-out so the two
  // cross-fade instead of running back to back.
  //
  // This component is HTML mounted in App.js, OUTSIDE the PIXI canvas, so it
  // paints over the loader rather than under it. Opening at boot (where the
  // rest of this mode's setup happens) showed the popup floating on the
  // loader's black background with the game appearing behind it afterwards;
  // waiting for the fade to fully finish instead left a visible gap. The
  // midpoint is the cue that the game underneath is already half-revealed —
  // driven by the loader animation's real progress, not a guessed delay.
  //
  // Ref-guarded so it fires exactly once per page load: without it, closing
  // the Enter modal would immediately satisfy this condition again and
  // reopen it, trapping the player. Nothing re-arms it — a standalone page
  // shows Enter once, then only ever the Ended modal.
  const standaloneEnterOpenedRef = useRef(false);
  useEffect(() => {
    if (!isStandaloneReplay || !loaderFadeMidpoint) return;
    if (standaloneEnterOpenedRef.current) return;
    standaloneEnterOpenedRef.current = true;
    dispatch(openReplayEnter());
  }, [isStandaloneReplay, loaderFadeMidpoint, dispatch]);

  // One column template shared by the header labels and every round row, so they
  // line up. Cols: Time&RoundID | Bet | Win | Multiplier | Play.
  // Table fonts — only the column header is 1px larger in landscape; the item
  // fonts stay the same as portrait. Landscape rows get a bit more vertical pad.
  const headerFontPx = isDesktop ? 14 : isPortrait ? 12 : 13;
  const rowFontPx = isDesktop ? 14 : isPortrait ? 9 : 9.5;
  const ridFontPx = isDesktop ? 12 : 8;
  // Empty/error body message — same scale family as the header labels, +2px.
  const emptyFontPx = isDesktop ? 16 : isPortrait ? 14 : 15;
  const rowPadYPx = isDesktop ? 9.5 : isPortrait ? 8 : 10;

  // Time gets the biggest share; Bet/Win/Multiplier are EQUAL fr so their
  // right-aligned edges are evenly spaced. Then an empty bonus column (spec's
  // bonus-icon slot, empty for non-bonus rounds — reads as a separator), then Play.
  // Landscape is wider, so Time needs a smaller share than in portrait.
  // Play button/column — bigger on desktop. The empty bonus/gap column is double
  // the play column on desktop.
  const playBtnPx = isDesktop ? 32 : 22;
  const bonusColPx = isDesktop ? playBtnPx * 2.5 : playBtnPx;
  // Chevron triangle size — a nudge bigger on desktop (button is bigger there).
  const triVPx = isDesktop ? 6 : 5;
  const triHPx = isDesktop ? 9 : 8;

  // Enter-modal element sizes — base ratios scaled uniformly. One multiplier
  // for the whole Enter/Ended family (fonts, buttons and the gaps below), so
  // every tier keeps identical proportions and only the overall size changes.
  // Wide desktop gets its own step up, same tier split as panelPad above.
  const enterScale = isWideDesktop ? 1.65 : isDesktop ? 1.5 : 1;
  // Body status states (loading spinner + label) — scale with the modal sizes.
  const spinnerPx = Math.round(38 * enterScale);
  const spinnerBorderPx = Math.max(2, Math.round(3 * enterScale));
  const statusFontPx = Math.round(11 * enterScale);
  // One gap for every row in the Enter/Ended modals. The outer column and the
  // nested played-at block both use it, so all rows sit evenly apart — tune
  // this single number to change the modal's rhythm.
  const enterRowGapPx = Math.round(18 * enterScale) - 4;
  const enterTitlePx = 33 * enterScale;
  // Play button runs 2% above the shared scale — its own nudge so the rest of
  // the modal keeps the sizes tuned above.
  const enterPlayPx = 72 * enterScale * 1.02;
  const enterPlayTriVPx = 15 * enterScale * 1.02;
  const enterPlayTriHPx = 23 * enterScale * 1.02;
  const enterBetPx = 20 * enterScale;
  const enterPlayedPx = 16 * enterScale;
  const enterTimePx = 20 * enterScale;
  // Ended-modal element sizes — same scale family as the Enter modal above.
  // Bet / Total Win / X Bet row — labels and values share this one size.
  const endedStatValuePx = 19 * enterScale;
  // REPLAY button runs 3% above the shared scale — its own nudge, applied to
  // font and padding alike so the button grows without changing shape.
  const endedReplayBtnScale = enterScale * 1.03;
  const endedReplayBtnFontPx = 18 * endedReplayBtnScale;
  const endedCopyLinkFontPx = 15 * enterScale;
  const endedCopyIconPx = 15 * enterScale;
  // Copy icon's back sheet: offset and stroke scale with the icon so the
  // two-sheet look holds at every tier instead of thinning out when it grows.
  const copyIconOffsetPx = Math.max(2, Math.round(endedCopyIconPx * 0.22));
  const copyIconStrokePx = Math.max(1.2, +(endedCopyIconPx * 0.09).toFixed(1));
  // Radius scales too — a fixed 2px reads as square once the icon grows.
  const copyIconRadiusPx = Math.max(2, Math.round(endedCopyIconPx * 0.16));
  // Front sheet sits slightly low so the back sheet's corner reads clearly and
  // the pair optically centres against the label.
  const copyIconFrontDropPx = Math.max(1, Math.round(endedCopyIconPx * 0.09));
  // The front sheet's black border eats into its own fill, so an equally-sized
  // back sheet looks bigger. Shrink the back by that border on both edges to
  // make the two read as the same size.
  const copyIconBackInsetPx = +(copyIconStrokePx * 2).toFixed(1);
  const gridCols = isPortrait
    ? `minmax(0,1.6fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.25fr) ${bonusColPx}px ${playBtnPx}px`
    : `minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,1.4fr) ${bonusColPx}px ${playBtnPx}px`;

  // Only build the (large) CSS string when the overlay is actually mounted —
  // the component still re-renders on every dimension change while closed.
  const overlayStyles = useMemo(
    () =>
      // Same bug as the render-gate above, one layer deeper: this used to
      // build the (large) CSS string only when the History board itself was
      // mounted, so the Enter/Ended modals rendered with an EMPTY <style>
      // tag whenever the board was already closed — exactly the case right
      // after a replay round finishes. Must build whenever ANY of the three
      // is mounted.
      shouldRender || enterShouldRender || endedShouldRender
        ? `
    .replay-overlay { font-family: 'Noto Sans', sans-serif; }

    /* Close button — absolutely positioned against the overlay (padding does not
       shift it), so it's independent of the panel's insets. btnAnchorTop mirrors
       the old header-title-row center; per-device nudges tweak from there. */
    .replay-close-btn {
      position: absolute;
      top: ${btnAnchorTop - closeBtnNudge.up}px; transform: translateY(-50%);
      right: ${closeButtonRight - closeBtnNudge.right}px;
      width: ${closeBtnPx}px; height: ${closeBtnPx}px;
      background-color: transparent; border: none; cursor: pointer; padding: 0;
      background-size: contain; background-repeat: no-repeat;
      background-position: center; transition: filter 0.2s ease;
      /* Kill the browser focus/tap outline that flickers on click (same as .spt-arrow). */
      outline: none;
      -webkit-tap-highlight-color: transparent;
      z-index: 1010;
    }
    .replay-close-btn:focus,
    .replay-close-btn:focus-visible { outline: none; }
    .replay-close-btn:hover { filter: brightness(2.0); }

    /* Panel — gray rectangle (#161616). Fills the overlay minus panelPad (applied
       as overlay padding in the inline style). Column layout so the header pins to
       the top and the (future) scrollable body takes the rest. */
    .replay-panel {
      width: 100%;
      height: 100%;
      background: #161616;
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    /* Header — pinned at the top, split into two equal horizontal halves:
       top = title (icon + "History"), bottom = column labels. Content lands later. */
    .replay-hdr {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
    }
    .replay-hdr-top {
      height: ${headerTopPx}px;
      background: #161616;
      display: flex;
      align-items: center;
      padding-left: 10px;
      gap: 8px;
    }
    .replay-hdr-icon {
      width: 30px;
      height: 30px;
      object-fit: contain;
      flex-shrink: 0;
    }
    .replay-hdr-title {
      color: #fefefe;
      font-size: 14px;
      font-weight: 700;
      white-space: nowrap;
    }
    .replay-hdr-bottom {
      height: ${headerBottomPx}px;
      background: #0e0e0e;
      display: grid;
      grid-template-columns: ${gridCols};
      align-items: center;
      padding: 0 6px;
      box-sizing: border-box;
      color: #fefefe;
      font-size: ${headerFontPx}px;
      font-weight: 600;
    }
    .replay-hdr-bottom > span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* Bet / Win / Multiplier labels align right within their column (Time stays left). */
    .replay-hdr-bottom > span:nth-child(n + 2) {
      text-align: right;
    }

    /* Body — everything below the header; scrollable, with a visible scrollbar
       pinned to the right. overflow-y:scroll keeps the bar shown even before
       there are enough rows to scroll. Table rows land here next. */
    .replay-body {
      flex: 1;
      min-height: 0;
      overflow-y: scroll;
      overflow-x: hidden;
      /* gap between rows and the scrollbar so it sits beside content, not over it */
      padding-right: 6px;
      scrollbar-width: thin;
      scrollbar-color: #9b9b9b #2a2a2a;
    }
    .replay-body::-webkit-scrollbar {
      width: 6px;
    }
    .replay-body::-webkit-scrollbar-track {
      background: #2a2a2a;
    }
    .replay-body::-webkit-scrollbar-thumb {
      background: #9b9b9b;
      border-radius: 3px;
    }

    /* ── Round rows — same column template as the header labels ── */
    .replay-row {
      display: grid;
      grid-template-columns: ${gridCols};
      align-items: center;
      padding: ${rowPadYPx}px 6px;
      box-sizing: border-box;
      font-size: ${rowFontPx}px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.09);
      cursor: pointer;
    }
    .replay-row:hover { background: #222222; }
    .replay-row > div { min-width: 0; }
    .replay-time {
      color: #fefefe;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .replay-rid {
      color: #6f6f6f;
      font-size: ${ridFontPx}px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .replay-cell-bet,
    .replay-cell-win,
    .replay-cell-mult {
      text-align: right;
    }
    .replay-cell-bet { color: #fefefe; }
    .replay-cell-win { color: #ffc003; }
    .replay-cell-mult { color: #60ab92; }
    /* Zero-win rounds: win + multiplier both muted gray. */
    .replay-cell-win.zero,
    .replay-cell-mult.zero { color: #6f6f6f; }
    .replay-cell-bonus { text-align: center; }
    /* ── Bigger than the row's text, on purpose ──

       A glyph at the row's own font size reads as punctuation next to the
       numbers rather than as the marker it is — their game's star is clearly
       an icon, not a character. Scaled off rowFontPx rather than written as a
       px value so it keeps its proportion on every device: the row is 14px on
       desktop and 9px in portrait, and a fixed size would be a tick on one and
       a blob on the other. */
    .replay-bonus-star {
      color: #ffc003;
      line-height: 1;
      font-size: ${Math.round(rowFontPx * 1.6)}px;
      display: inline-block;
    }
    .replay-cell-play { display: flex; justify-content: flex-end; }
    .replay-play {
      width: ${playBtnPx}px;
      height: ${playBtnPx}px;
      background: rgba(255, 255, 255, 0.09);
      border: none;
      border-radius: 30%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }
    .replay-play::before {
      content: "";
      width: 0;
      height: 0;
      border-style: solid;
      border-width: ${triVPx}px 0 ${triVPx}px ${triHPx}px;
      border-color: transparent transparent transparent #fefefe;
      transition: border-left-color 0.15s ease;
    }
    .replay-play:hover { background: rgba(255, 255, 255, 0.16); }
    /* Chevron turns win-gold on hover. */
    .replay-play:hover::before { border-left-color: #ffc003; }

    /* ── Body status states: loading spinner (fetch in flight) ── */
    .replay-status {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: ${Math.round(12 * enterScale)}px;
      color: #6f6f6f;
      font-size: ${statusFontPx}px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      text-align: center;
      padding: 0 16px;
    }
    .replay-spinner {
      width: ${spinnerPx}px;
      height: ${spinnerPx}px;
      border-radius: 50%;
      /* Full ring in track gray, one arc in win-gold — rotates. */
      border: ${spinnerBorderPx}px solid #2a2a2a;
      border-top-color: #ffc003;
      animation: replay-spin 0.9s linear infinite;
    }
    @keyframes replay-spin {
      to { transform: rotate(360deg); }
    }

    /* ── Body status states: empty list / fetch failed — plain sentence, no
       spinner, sitting near the top of the body (not vertically centered).
       Same muted gray as the loading label, sentence case (not uppercase). ── */
    .replay-empty {
      width: 100%;
      color: #6f6f6f;
      font-size: ${emptyFontPx}px;
      font-weight: 400;
      text-align: center;
      padding: 28px 16px 0;
      box-sizing: border-box;
    }

    /* ── Enter modal — pitch-black variant of the panel, shown over History.
       Same size/position (overlay padding = panelPad) and same close-button spot. ── */
    .replay-enter {
      width: 100%;
      height: 100%;
      background: #000;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #fefefe;
      gap: ${enterRowGapPx}px;
      padding: 0 16px;
    }
    .replay-enter-title {
      font-size: ${enterTitlePx}px;
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    /* Reuses .replay-play (bg, radius, hover), just bigger + bigger chevron. */
    .replay-enter-play {
      width: ${enterPlayPx}px;
      height: ${enterPlayPx}px;
    }
    .replay-enter-play::before {
      border-width: ${enterPlayTriVPx}px 0 ${enterPlayTriVPx}px ${enterPlayTriHPx}px;
    }
    /* Bet / played / time grouped with a tighter gap than the parent. */
    .replay-enter-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: ${enterRowGapPx}px;
    }
    .replay-enter-bet { font-size: ${enterBetPx}px; }
    .replay-enter-bet-label { color: #6f6f6f; }
    .replay-enter-played {
      color: #6f6f6f;
      font-size: ${enterPlayedPx}px;
    }
    .replay-enter-time {
      font-size: ${enterTimePx}px;
      font-weight: 700;
    }

    /* ── Ended modal — same black frame as the Enter modal (.replay-enter),
       shown once a replay finishes: Bet / Total Win / X Bet — a row on
       landscape/desktop, a column on portrait. ──

       ONE LINE, ALWAYS. Nothing here wraps and nothing breaks: not the row
       between stats, not a stat between its label and its value, not a value
       between its amount and its currency. When the three do not fit, they are
       scaled down TOGETHER until they do — see endedStatsScale, which measures
       what the row wanted to be and hands the ratio back here.

       Stretched to the full width on purpose: as a shrink-to-fit item the row
       would be exactly as wide as its contents, the measurement could never see
       an overflow, and the text would simply run out of the modal. */
    .replay-ended-stats {
      display: flex;
      flex-direction: ${isPortrait ? "column" : "row"};
      flex-wrap: nowrap;
      justify-content: center;
      align-items: ${isPortrait ? "center" : "baseline"};
      align-self: stretch;
      width: 100%;
      box-sizing: border-box;
      gap: ${isPortrait ? Math.round(10 * enterScale) : Math.round(28 * enterScale)}px;
      /* The fit-to-width scale is applied INLINE, not here: this stylesheet is
         memoised on the layout inputs, and rebuilding the whole string on a
         measurement would be both wasteful and a stale-dependency trap. */
      transform-origin: center center;
    }
    /* Label and value belong together and never come apart — see above. */
    .replay-ended-stat {
      white-space: nowrap;
    }
    .replay-ended-stat-label {
      color: #6f6f6f;
      /* Matches the value it labels in both size and weight — colour alone
         separates the two, as in the reference. */
      font-size: ${endedStatValuePx}px;
      font-weight: 600;
    }
    .replay-ended-stat-value {
      font-size: ${endedStatValuePx}px;
      font-weight: 600;
      margin-left: 4px;
    }
    /* Total Win reads as the payout figure, so it takes the same gold as the
       REPLAY button rather than plain white. */
    .replay-ended-stat-value.is-win { color: #ffc003; }
    .replay-ended-replay-btn {
      background: #ffc003;
      color: #161616;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      padding: ${Math.round(14 * endedReplayBtnScale)}px ${Math.round(44 * endedReplayBtnScale)}px;
      font-size: ${endedReplayBtnFontPx}px;
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
      transition: filter 0.15s ease;
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }
    .replay-ended-replay-btn:hover { filter: brightness(0.92); }
    .replay-ended-copy-link {
      display: flex;
      align-items: center;
      gap: ${Math.round(8 * enterScale)}px;
      background: none;
      border: none;
      cursor: pointer;
      color: #6f6f6f;
      font-size: ${endedCopyLinkFontPx}px;
      font-weight: 500;
      padding: 0;
      transition: color 0.15s ease;
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }
    .replay-ended-copy-link:hover { color: #ffffff; }
    /* Two sheets. The element itself is just the layout box — ::before is the
       back sheet (offset up-right, outline only) and ::after the front sheet,
       so normal paint order puts the front on top. A pseudo-element can't be
       painted behind its own parent's background, which is why neither sheet
       is drawn on the element itself. */
    .replay-copy-icon {
      position: relative;
      width: ${endedCopyIconPx}px;
      height: ${endedCopyIconPx}px;
      flex-shrink: 0;
    }
    .replay-copy-icon::before {
      content: "";
      position: absolute;
      box-sizing: border-box;
      top: -${copyIconOffsetPx}px;
      right: -${copyIconOffsetPx}px;
      width: calc(100% - ${copyIconBackInsetPx}px);
      height: calc(100% - ${copyIconBackInsetPx}px);
      border: ${copyIconStrokePx}px solid currentColor;
      border-radius: ${copyIconRadiusPx}px;
    }
    .replay-copy-icon::after {
      content: "";
      position: absolute;
      box-sizing: border-box;
      top: ${copyIconFrontDropPx}px;
      left: 0;
      width: 100%;
      height: 100%;
      background: currentColor;
      /* Black border separates the filled front sheet from the outline
         behind it, instead of the two merging into one blob. */
      border: ${copyIconStrokePx}px solid #000;
      border-radius: ${copyIconRadiusPx}px;
    }
  `
        : "",
    [
      shouldRender,
      enterShouldRender,
      endedShouldRender,
      btnAnchorTop,
      closeButtonRight,
      closeBtnPx,
      closeBtnNudge,
      headerTopPx,
      headerBottomPx,
      gridCols,
      headerFontPx,
      rowFontPx,
      ridFontPx,
      rowPadYPx,
      playBtnPx,
      triVPx,
      triHPx,
      enterRowGapPx,
      enterTitlePx,
      enterPlayPx,
      enterPlayTriVPx,
      enterPlayTriHPx,
      enterBetPx,
      enterPlayedPx,
      enterTimePx,
      enterScale,
      spinnerPx,
      spinnerBorderPx,
      statusFontPx,
      emptyFontPx,
      endedStatValuePx,
      endedReplayBtnScale,
      endedReplayBtnFontPx,
      endedCopyLinkFontPx,
      endedCopyIconPx,
      copyIconOffsetPx,
      copyIconStrokePx,
      copyIconRadiusPx,
      copyIconFrontDropPx,
      copyIconBackInsetPx,
      isPortrait,
    ],
  );

  // Bail out only when NOTHING needs to render. Was `if (!shouldRender)
  // return null`, which also killed the Enter/Ended modals below whenever
  // the History board itself was closed — exactly the case right after a
  // replay round finishes (handleEnterPlay already closed the board to
  // reveal the game), so the Ended modal could never actually appear even
  // though openReplayEnded() had already fired correctly.
  if (!shouldRender && !enterShouldRender && !endedShouldRender) return null;

  return (
    <>
    {shouldRender && (
    <div
      ref={overlayRef}
      className="replay-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        // Visible-area size (excludes browser toolbars) — dimensions.* come from
        // visualViewport, matching PaytableOverlay.
        width: dimensions.width,
        height: dimensions.height,
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.97) 100%)",
        zIndex: 1000,
        // Panel insets — top/bottom set its height, side sets its width.
        padding: `${panelPad.top}px ${panelPad.side}px ${panelPad.bottom}px`,
        boxSizing: "border-box",
        // Starts hidden; animejs drives opacity from the fade effects above.
        opacity: 0,
        // visibility (not just opacity) so the whole subtree stops hit-testing
        // while the iOS fullscreen spine is up — same reasoning as PaytableOverlay.
        visibility: isHidden ? "hidden" : "visible",
        pointerEvents: isHidden ? "none" : "auto",
      }}
    >
      {/* Close button — absolute, anchored to the overlay */}
      <button
        className="replay-close-btn"
        onClick={handleClose}
        style={{
          backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
        }}
      />

      {/* Gray panel — header (split in half) pinned on top, body below */}
      <div className="replay-panel">
        <div className="replay-hdr">
          <div className="replay-hdr-top">
            {historyIconSrc ? (
              <img className="replay-hdr-icon" src={historyIconSrc} alt="" />
            ) : null}
            <span className="replay-hdr-title">{t("replayHistory_title")}</span>
          </div>
          <div className="replay-hdr-bottom">
            <span>{t("replayHistory_col_time_round_id")}</span>
            <span>{t("replayHistory_col_bet")}</span>
            <span>{t("replayHistory_col_win")}</span>
            <span>{t("replayHistory_col_multiplier")}</span>
            <span />
            <span />
          </div>
        </div>
        <div className="replay-body">
          {historyLoading ? (
            <div className="replay-status">
              <div className="replay-spinner" />
              <div>{t("replayHistory_loading")}</div>
            </div>
          ) : historyError ? (
            <div className="replay-empty">
              {t("replayHistory_load_error")}
            </div>
          ) : historyEmpty ? (
            <div className="replay-empty">
              {t("replayHistory_empty")}
            </div>
          ) : (
            rounds.map((r, i) => {
              const isWin = r.win > 0;
              return (
                <div className="replay-row" key={r.roundId || i}>
                  <div className="replay-cell-time">
                    <div className="replay-time">{formatRowTime(r.time)}</div>
                    <div className="replay-rid">
                      {shortenRoundId(r.roundId)}
                    </div>
                  </div>
                  <div className="replay-cell-bet">
                    {`${formatToCustomLocale(r.bet)} ${currency}`}
                  </div>
                  <div className={"replay-cell-win" + (isWin ? "" : " zero")}>
                    {`${formatToCustomLocale(r.win)} ${currency}`}
                  </div>
                  <div className={"replay-cell-mult" + (isWin ? "" : " zero")}>
                    {formatMultiplier(r.win, r.initialBet || r.bet)}
                  </div>
                  <div className="replay-cell-bonus">
                    {/* ── A BOUGHT round, marked the way their own game marks it ──

                        Observed 2026-09-10 on a PopiPlay reference row:
                        `14.50 FUN | 31.88 FUN | x318.80 | star`. It is what
                        makes the row readable — the Bet column shows the PRICE
                        and the multiplier is against the STAKE, so without it
                        the two numbers look unrelated.

                        ── ANY truthy `feature`, NOT the literal "bonus_buy" ──

                        This was briefly narrowed to `=== "bonus_buy"` and the
                        star vanished from real bought rounds. Mad Labs does not
                        use that name: the API advertises its buy feature under
                        its own string and rejects the hardcoded "bonus_buy"
                        with 203 invalid_options — see selectBonusBuyFeatureName
                        in BuyBonusPopup. So the literal can never match here.

                        The server sets `feature` only on a round that HAD one,
                        so its presence is the signal and its value is the
                        operator's business, not ours.

                        The GLYPH is ours, not their asset — a system-font star,
                        so it can differ slightly across platforms. Swap it for
                        their icon if one can be had. */}
                    {r.feature ? (
                      <span className="replay-bonus-star" aria-label={r.feature}>
                        ★
                      </span>
                    ) : null}
                  </div>
                  <div className="replay-cell-play">
                    <button
                      className="replay-play"
                      aria-label={t("replayHistory_play_aria")}
                      title={t("replayHistory_play_aria")}
                      onClick={() => handlePlay(r)}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
    )}

    {/* Overlay-scoped styles — used by the History board above AND the
       Enter/Ended modals below (all their classes live in this one
       stylesheet), so this must render whenever ANY of the three do, not
       just when the History board itself is open. */}
    <style>{overlayStyles}</style>

    {/* Enter modal — pitch-black, on top of History; X closes only this. */}
    {enterShouldRender && enterRow && (
      <div
        ref={enterRef}
        className="replay-enter-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: dimensions.width,
          height: dimensions.height,
          // Light semi-transparent dim — it stacks over History's own heavy
          // backdrop, so only the centered panel is full black (#000).
          background: "rgba(0,0,0,0.85)",
          zIndex: 1100,
          padding: `${panelPad.top}px ${panelPad.side}px ${panelPad.bottom}px`,
          boxSizing: "border-box",
          // Starts hidden; animejs drives opacity from the fade effects above.
          opacity: 0,
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isHidden ? "none" : "auto",
        }}
      >
        {/* Close button — identical spot to History's, closes only this modal */}
        <button
          className="replay-close-btn"
          onClick={handleEnterClose}
          style={{
            backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
          }}
        />

        <div className="replay-enter">
          <div className="replay-enter-title">{gameTitle}</div>
          <button
            className="replay-play replay-enter-play"
            aria-label={t("replayHistory_play_aria")}
            title={t("replayHistory_play_aria")}
            onClick={handleEnterPlay}
          />
          <div className="replay-enter-info">
            <div className="replay-enter-bet">
              <span className="replay-enter-bet-label">
                {t("replayHistoryModal_bet_label")}
              </span>{" "}
              {`${formatToCustomLocale(enterRow.bet)} ${currency}`}
            </div>
            <div className="replay-enter-played">
              {t("replayHistoryModal_played_label")}
            </div>
            <div className="replay-enter-time">
              {formatPlayedAt(enterRow.time, replayRoundId)}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Ended modal — same black frame as Enter, shown once a replay finishes.
       X closes only this modal (History stays open behind it). */}
    {endedShouldRender && endedRow && (
      <div
        ref={endedRef}
        className="replay-enter-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: dimensions.width,
          height: dimensions.height,
          background: "rgba(0,0,0,0.85)",
          zIndex: 1100,
          padding: `${panelPad.top}px ${panelPad.side}px ${panelPad.bottom}px`,
          boxSizing: "border-box",
          opacity: 0,
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isHidden ? "none" : "auto",
        }}
      >
        <button
          className="replay-close-btn"
          onClick={handleEndedClose}
          style={{
            backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
          }}
        />

        <div className="replay-enter">
          <div className="replay-enter-title">{gameTitle}</div>
          <div className="replay-enter-info">
            <div className="replay-enter-played">
              {t("replayHistoryEndedModal_played_label")}
            </div>
            <div className="replay-enter-time">
              {formatPlayedAt(endedRow.time, replayRoundId)}
            </div>
          </div>
          <div
            className="replay-ended-stats"
            ref={endedStatsRef}
            style={{ transform: `scale(${endedStatsScale})` }}
          >
            <div className="replay-ended-stat">
              <span className="replay-ended-stat-label">
                {t("replayHistoryEndedModal_bet_label")}
              </span>
              <span className="replay-ended-stat-value">
                {`${formatToCustomLocale(endedRow.bet)} ${currency}`}
              </span>
            </div>
            <div className="replay-ended-stat">
              <span className="replay-ended-stat-label">
                {t("replayHistoryEndedModal_total_win_label")}
              </span>
              <span className="replay-ended-stat-value is-win">
                {`${formatToCustomLocale(endedRow.win)} ${currency}`}
              </span>
            </div>
            <div className="replay-ended-stat">
              <span className="replay-ended-stat-label">
                {t("replayHistoryEndedModal_x_bet_label")}
              </span>
              <span className="replay-ended-stat-value">
                {formatMultiplier(endedRow.win, endedRow.initialBet || endedRow.bet)}
              </span>
            </div>
          </div>
          <button
            className="replay-ended-replay-btn"
            aria-label={t("replayHistory_play_aria")}
            title={t("replayHistory_play_aria")}
            onClick={handleReplayAgain}
          >
            {t("replayHistoryEndedModal_replay_btn")}
          </button>
          {/* Needs a link to copy. In-game rows carry one; a standalone
              replay page's synthetic round has neither replayLink nor
              replayUrl (no history row behind it) but IS itself a shared
              replay link, so handleCopyLink hands back the page URL. Without
              either, the button would look active and silently do nothing. */}
          {(isStandaloneReplay ||
            endedRow.replayLink ||
            endedRow.replayUrl) && (
            <button
              className="replay-ended-copy-link"
              onClick={handleCopyLink}
              aria-label={t(
                "replayHistoryEndedModal_copy_link_aria",
                "Copy replay link to clipboard",
              )}
            >
              <span className="replay-copy-icon" aria-hidden="true" />
              <span>{t("replayHistoryEndedModal_copy_link")}</span>
            </button>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default ReplayOverlay;
